import React from 'react';

import { SortHeader } from '../../components/ui/SortHeader.jsx';
import { EXCLUSION_LABEL, GAP_LABEL } from '../../constants/exclusions.js';
import { COL_DEFAULT_DIR } from './constants.js';
import { RecordDetailRow } from './RecordDetailRow.jsx';
import { renderRateMix, statusLabel } from './helpers.jsx';

// COLSPAN matches the visible column count. Drops by 1 when the select
// checkbox is hidden, and by 1 again when the last "Gaps/Error/Excluded"
// column is hidden (tabs where mapping_gaps is meaningless by definition).
// Pending records have no gaps either — gap records go to status='gap'.
const TABS_WITHOUT_LAST_COL = new Set(['pending', 'approved', 'queued', 'pushed']);

export function RecordsTable({
  filter,                   // active tab — drives column headers/visibility
  records, total, hasMore, loading, loadingMore,
  selected, onToggleSelect, onSelectAll,
  // "Select all in filter": when on, ``selected`` holds the rows the user
  // UN-ticked out of the whole filter rather than the ones they ticked.
  selectAllMatching = false,
  selectionCount = 0,
  onSelectAllMatching, onClearSelection,
  expandedId, detailCache, onExpand,
  // ``setSort(col, dir)`` writes both halves in one go — they live in the URL
  // and two separate writes would leave a half-applied sort in the address bar.
  sortBy, sortDir, setSort,
  sentinelRef,
  selectable = true,        // when false, hide the checkbox column entirely
}) {
  let COLSPAN = selectable ? 13 : 12;
  if (TABS_WITHOUT_LAST_COL.has(filter)) COLSPAN -= 1;
  const onSort = (col) => {
    if (sortBy === col) setSort(col, sortDir === 'asc' ? 'desc' : 'asc');
    else setSort(col, COL_DEFAULT_DIR[col] || 'desc');
  };
  // Which row is open travels in the URL as the durable record_key, so a
  // shared "here is the list, with this row open" link still opens the same
  // row after a reprocess. Falls back to the id for a row the API returned
  // without a key (an incomplete natural key), and matches either form so an
  // older id-bearing link keeps working for the rest of the session.
  const isExpanded = (r) => expandedId
    && (String(expandedId) === String(r.record_key) || String(expandedId) === String(r.id));

  const Header = (props) => (
    <SortHeader
      sortBy={sortBy} sortDir={sortDir} onClick={onSort}
      {...props}
    />
  );

  if (loading) return <div className="alert alert-info">Loading...</div>;
  if (records.length === 0) return <div className="alert alert-info">No records found for this filter.</div>;

  // The page can only tick what it has rendered (100 rows at a time). Once
  // every rendered row is ticked and more match, offer the whole filter —
  // otherwise clearing 3,000 gaps means scrolling and re-ticking 30 times.
  const pageFullySelected = !selectAllMatching
    && records.length > 0 && selected.size === records.length;
  const showBanner = selectable
    && (selectAllMatching || (pageFullySelected && total > records.length));

  return (
    <div style={{ overflowX: 'auto' }}>
      {showBanner && (
        <div style={{
          padding: '8px 12px', marginBottom: 8, borderRadius: 6, fontSize: 13,
          background: selectAllMatching ? '#fff4e5' : '#e7f0fb',
          color: selectAllMatching ? '#8a4b00' : '#0a4ea0',
          border: `1px solid ${selectAllMatching ? '#f0c489' : '#b8d4f0'}`,
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
        }}>
          {selectAllMatching ? (
            <>
              <span>
                All <strong>{selectionCount.toLocaleString()}</strong> records matching
                this filter are selected
                {selected.size > 0 && (
                  <span> ({selected.size.toLocaleString()} un-ticked)</span>
                )}
                . Actions apply to every one of them, including rows not shown here.
              </span>
              <button
                type="button" className="btn btn-sm btn-secondary"
                onClick={onClearSelection}
              >
                Clear selection
              </button>
            </>
          ) : (
            <>
              <span>
                All <strong>{records.length}</strong> records on this page are selected.
              </span>
              <button
                type="button" className="btn btn-sm btn-primary"
                onClick={onSelectAllMatching}
              >
                Select all {total.toLocaleString()} matching this filter
              </button>
            </>
          )}
        </div>
      )}
      <table>
        <thead>
          <tr>
            {selectable && (
              <th>
                <input
                  type="checkbox" className="cb"
                  onChange={onSelectAll}
                  checked={selectAllMatching
                    || (selected.size === records.length && records.length > 0)}
                />
              </th>
            )}
            <th>Punch</th>
            <Header col="employee_name">Employee</Header>
            <Header col="pay_group">Pay Group</Header>
            <Header col="business_date">Date</Header>
            <Header col="gross_hours">Hours</Header>
            <Header col="ns_location_name">Location</Header>
            <Header col="billing_title_name">Title</Header>
            <Header col="service_item_name">Service Item</Header>
            <th>Rate</th>
            <Header col="status">Status</Header>
            {/* Last column varies by tab; hidden entirely on tabs where the
                record by definition has no gap / no exclusion / no error. */}
            {(() => {
              if (TABS_WITHOUT_LAST_COL.has(filter)) return null;
              const label = filter === 'failed' ? 'NetSuite Error'
                          : filter === 'excluded_ref_data' ? 'Excluded By'
                          : filter === 'ops_review' ? 'Drift'
                          : 'Gaps';
              return <th>{label}</th>;
            })()}
            <th />
          </tr>
        </thead>
        <tbody>
          {records.map((r) => (
            <React.Fragment key={r.id}>
              <tr style={{ cursor: 'pointer' }} onClick={() => onExpand(r)}>
                {selectable && (
                  <td onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox" className="cb"
                      // Filter mode inverts the Set: a row is selected unless
                      // it's in the un-tick list.
                      checked={selectAllMatching ? !selected.has(r.id) : selected.has(r.id)}
                      onChange={() => onToggleSelect(r.id)}
                    />
                  </td>
                )}
                <td>
                  {r.punch_xref_code}
                  {r.source_type === 'pay_adjustment' && (
                    <div style={{ fontSize: 10, color: '#999' }}>Pay Adjustment</div>
                  )}
                </td>
                <td style={{ fontWeight: 500 }}>{r.employee_name || r.employee_xref_code}</td>
                {/* Dayforce pay group — the employee's payroll calendar (BEY,
                    MMH, ...). Blank when the employee isn't in the synced map
                    yet (new hire between syncs). */}
                <td>
                  {r.pay_group ? (
                    <span style={{
                      display: 'inline-block', padding: '1px 6px', borderRadius: 3,
                      background: '#eef1f6', color: '#3c4a63',
                      fontWeight: 600, fontSize: 11, letterSpacing: 0.3,
                    }}>
                      {r.pay_group}
                    </span>
                  ) : <span style={{ color: '#bbb' }}>-</span>}
                </td>
                <td>{r.business_date}</td>
                <td>
                  {r.gross_hours}h
                  {/* Near-whole week rounding: this record carried its week's
                      correction, so the hours shown are not verbatim Dayforce.
                      Say so here rather than making a reviewer diff against
                      df.pay_summaries. */}
                  {r.hours_rounding_delta != null
                   && Number(r.hours_rounding_delta) !== 0 ? (
                    <div style={{ fontSize: 10, marginTop: 2 }}>
                      <span
                        title={'This record carried its billing week\u2019s rounding '
                          + 'correction \u2014 the week\u2019s Dayforce hours landed a hair '
                          + 'off a whole number, so the whole adjustment was applied here.'}
                        style={{
                          display: 'inline-block', padding: '1px 5px', borderRadius: 3,
                          background: '#eef6ec', color: '#2f6b2a', fontWeight: 600,
                        }}
                      >
                        Rounded {Number(r.hours_rounding_delta) > 0 ? '+' : '−'}
                        {Math.abs(Number(r.hours_rounding_delta)).toFixed(3)}
                      </span>
                    </div>
                  ) : null}
                  {/* Weekly minute balance: a minute was shifted between two
                      days of this week so the invoice's weekly column adds up.
                      0 is a real value (gave on one rate level, took on
                      another) — only null means untouched. */}
                  {r.minutes_moved != null ? (
                    <div style={{ fontSize: 10, marginTop: 2 }}>
                      <span
                        title={'A minute was moved between two days of this week so the '
                          + 'weekly invoice column adds up \u2014 NetSuite bills each day at '
                          + 'two decimals, which can leave the week reading 40.01 or 39.99. '
                          + 'The week\u2019s total and its ST/OT split are unchanged.'}
                        style={{
                          display: 'inline-block', padding: '1px 5px', borderRadius: 3,
                          background: '#eef2fa', color: '#2c4a7c', fontWeight: 600,
                        }}
                      >
                        {Number(r.minutes_moved) === 0
                          ? 'Minute balance \u00b10'
                          : `Minute balance ${Number(r.minutes_moved) > 0 ? '+' : '\u2212'}${Math.abs(Number(r.minutes_moved))}`}
                      </span>
                    </div>
                  ) : null}
                </td>
                <td>
                  {r.ns_location_name || r.site_code}
                  {/* Canada mirror: employee homed outside Canada working a
                      9xxxx site. The TimeBill books against the mirror
                      location, so surface both here. */}
                  {r.is_loaned_employee ? (
                    <div style={{ fontSize: 10, marginTop: 2 }}>
                      <span style={{
                        display: 'inline-block', padding: '1px 5px', borderRadius: 3,
                        background: '#e8eefc', color: '#29417a', fontWeight: 600,
                      }}>
                        Loaned
                      </span>
                      {r.mirror_location_name ? (
                        <span style={{ color: '#666', marginLeft: 4 }}>
                          → {r.mirror_location_name}
                        </span>
                      ) : (
                        <span style={{ color: '#b25b00', marginLeft: 4 }}>
                          no mirror location
                        </span>
                      )}
                    </div>
                  ) : null}
                </td>
                <td>{r.billing_title_name || '-'}</td>
                <td>{r.service_item_name || '-'}</td>
                <td>{renderRateMix(r.rate_levels, r.status)}</td>
                <td><span className={`badge ${r.status}`}>{statusLabel(r.status)}</span></td>
                {!TABS_WITHOUT_LAST_COL.has(filter) && (
                <td style={{ fontSize: 11, color: '#999' }}>
                  {r.exclusion_reason ? (
                    <span style={{ color: '#3c3f7c', fontWeight: 500 }}>
                      {EXCLUSION_LABEL[r.exclusion_reason] || r.exclusion_reason}
                    </span>
                  ) : r.status === 'source_modified' ? (
                    <span style={{ color: '#b25b00', fontWeight: 500 }}>
                      ⚠ Content drift — expand row to compare
                    </span>
                  ) : r.status === 'source_deleted' ? (
                    <span style={{ color: '#b25b00', fontWeight: 500 }}>
                      ⚠ Source punch deleted in Dayforce
                    </span>
                  ) : r.status === 'manually_handled' ? (
                    <span style={{ color: '#1c5d54', fontWeight: 500 }}>
                      Billed outside this app — excluded from every automatic pass
                    </span>
                  ) : r.status === 'failed' && r.latest_push_error ? (
                    <span
                      style={{
                        color: '#c00', fontWeight: 500,
                        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                      }}
                      title={r.latest_push_error}
                    >
                      {r.latest_push_error.length > 140
                        ? `${r.latest_push_error.slice(0, 140)}…`
                        : r.latest_push_error}
                    </span>
                  ) : (
                    <>
                      <span>{(r.mapping_gaps || []).map((g) => GAP_LABEL[g] || g).join(', ')}</span>
                      {r.status === 'gap'
                        && r.billable_hours_at_stake != null
                        && r.billable_hours_at_stake > 0 && (
                          <span style={{
                            display: 'block', color: '#FF6600',
                            fontWeight: 600, marginTop: 2,
                          }}>
                            {r.billable_hours_at_stake}h at stake
                          </span>
                        )}
                    </>
                  )}
                </td>
                )}
                <td style={{ fontSize: 11, color: '#29417a', whiteSpace: 'nowrap' }}>
                  {isExpanded(r) ? '[-]' : '[+]'}
                  {/* The record on its own page — a link you can paste into a
                      ticket or an email without sending the whole table with
                      it. A real href, so middle-click and "open in new tab"
                      both work. */}
                  {/* record_key, never id: ids are rebuilt by every
                      reprocess, so an id link is dead by morning. */}
                  <a
                    href={r.record_key ? `/records/k/${r.record_key}` : `/records/${r.id}`}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    title="Open this record on its own page (new tab)"
                    style={{ marginLeft: 8, textDecoration: 'none', fontSize: 13 }}
                  >
                    ↗
                  </a>
                </td>
              </tr>
              {isExpanded(r) && detailCache[r.id] && (
                <RecordDetailRow detail={detailCache[r.id]} colSpan={COLSPAN} />
              )}
              {isExpanded(r) && !detailCache[r.id] && (
                <tr>
                  <td colSpan={COLSPAN} style={{ textAlign: 'center', padding: 12, color: '#666' }}>
                    Loading...
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
      <div ref={sentinelRef} style={{ height: 1 }} />
      {loadingMore && (
        <div style={{ textAlign: 'center', padding: 12, color: '#666', fontSize: 13 }}>
          Loading more...
        </div>
      )}
      {!hasMore && records.length > 0 && (
        <div style={{ textAlign: 'center', padding: 12, color: '#999', fontSize: 12 }}>
          All {total.toLocaleString()} records loaded
        </div>
      )}
    </div>
  );
}
